import React, { useState, useEffect, useCallback } from 'react';
import {
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CreditCardIcon,
  ArrowTrendingUpIcon,
  WalletIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { listAllSettlements } from '../api/superAdminApi';

const MarketplaceFinancePage: React.FC = () => {
  const [settlements, setSettlements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'pending' | 'paid'>('pending');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listAllSettlements({ status: filter });
      setSettlements(response.data?.items || response.data || response.items || response || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load settlements');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (n: number) => new Intl.NumberFormat('en-RW', { style: 'currency', currency: 'RWF', minimumFractionDigits: 0 }).format(n || 0);

  const totalPending = settlements.filter((s: any) => s.status === 'pending').reduce((sum: number, s: any) => sum + (s.supplier_payout || s.supplierPayout || 0), 0);
  const totalPaid = settlements.filter((s: any) => s.status === 'paid').reduce((sum: number, s: any) => sum + (s.supplier_payout || s.supplierPayout || 0), 0);
  const totalCommissions = settlements.reduce((sum: number, s: any) => sum + (s.platform_commission || s.platformCommission || 0), 0);

  return (
    <div className="space-y-6">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-2xl p-8 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full -translate-y-1/2 translate-x-1/4"></div>
        </div>
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <WalletIcon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">Finance & Settlements</h1>
              <p className="text-orange-100 mt-1">Manage supplier payouts and commissions</p>
            </div>
          </div>
          <button onClick={fetchData} className="flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 font-medium transition-all">
            <ArrowPathIcon className="w-5 h-5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="relative overflow-hidden bg-white rounded-2xl border border-gray-100 p-6 shadow-lg shadow-amber-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Pending Payouts</p>
              <p className="text-3xl font-extrabold text-amber-600 mt-2">{fmt(totalPending)}</p>
              <p className="text-xs text-gray-400 mt-1">{settlements.filter((s: any) => s.status === 'pending').length} settlements</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
              <ClockIcon className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
        <div className="relative overflow-hidden bg-white rounded-2xl border border-gray-100 p-6 shadow-lg shadow-emerald-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Paid Out</p>
              <p className="text-3xl font-extrabold text-emerald-600 mt-2">{fmt(totalPaid)}</p>
              <p className="text-xs text-gray-400 mt-1">{settlements.filter((s: any) => s.status === 'paid').length} settlements</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg">
              <CheckCircleIcon className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
        <div className="relative overflow-hidden bg-white rounded-2xl border border-gray-100 p-6 shadow-lg shadow-violet-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Platform Commissions</p>
              <p className="text-3xl font-extrabold text-violet-600 mt-2">{fmt(totalCommissions)}</p>
              <p className="text-xs text-gray-400 mt-1">Total earned</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center shadow-lg">
              <ArrowTrendingUpIcon className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <FunnelIcon className="w-5 h-5 text-gray-400" />
        {(['pending', 'paid'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-5 py-2.5 text-sm font-semibold rounded-xl transition-all ${
              filter === status
                ? 'bg-orange-600 text-white shadow-lg shadow-orange-200'
                : 'bg-white text-gray-600 border border-gray-200 hover:border-orange-300 hover:text-orange-600'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-orange-200 rounded-full"></div>
            <div className="w-12 h-12 border-4 border-orange-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto" />
          <p className="text-red-700 font-semibold mt-3">{error}</p>
          <button onClick={fetchData} className="mt-4 px-5 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium">Retry</button>
        </div>
      ) : settlements.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-12 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-orange-100 to-amber-100 rounded-2xl flex items-center justify-center mx-auto">
            <CurrencyDollarIcon className="w-10 h-10 text-orange-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mt-4">No {filter} settlements</h3>
          <p className="text-gray-500 mt-2">
            {filter === 'pending' ? 'All settlements are up to date' : 'No settlements have been paid yet'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Supplier</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Order</th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Sale</th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Commission</th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Payout</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Due</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  {filter === 'pending' && (
                    <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Action</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {settlements.map((s: any) => (
                  <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-bold text-gray-900 text-sm">{s.supplier_name || s.supplierOrganisation || 'N/A'}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{s.order_number || s.orderNumber || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 text-right">{fmt(s.total_sale || s.totalSale || 0)}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-violet-600 text-right">{fmt(s.platform_commission || s.platformCommission || 0)}</td>
                    <td className="px-6 py-4 text-sm font-bold text-emerald-700 text-right">{fmt(s.supplier_payout || s.supplierPayout || 0)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{s.due_date || s.dueDate ? new Date(s.due_date || s.dueDate).toLocaleDateString() : 'N/A'}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${
                        s.status === 'pending' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      }`}>
                        {s.status}
                      </span>
                    </td>
                    {filter === 'pending' && (
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => {
                            setActionLoading(s.id);
                            setTimeout(() => { setActionLoading(null); fetchData(); }, 1000);
                          }}
                          disabled={actionLoading === s.id}
                          className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-sm rounded-xl hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-50 font-bold shadow-md shadow-emerald-200 transition-all"
                        >
                          <CreditCardIcon className="w-4 h-4" />
                          {actionLoading === s.id ? 'Processing...' : 'Mark Paid'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplaceFinancePage;
